import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Tooltip,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  styled,
  Alert,
  Card,
  CardContent,
  Avatar,
  Button,
} from "@mui/material";
import {
  School,
  Business,
  CheckCircle,
  Cancel,
  AccountBalance,
  BarChart,
  ExpandMore,
  ExpandLess,
  Refresh,
  Domain,
  LocationCity,
  TrendingUp,
  GetApp,
} from "@mui/icons-material";
import axios from "axios";
import { baseUrl } from "../../common/utils";

// ============================================
// Styled Components - Matching SchoolManagement style
// ============================================
const StatsContainer = styled(Paper)(({ theme }) => ({
  padding: "24px",
  borderRadius: "8px",
  border: "1px solid #e5e7eb",
  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  backgroundColor: "#ffffff",
  margin: "16px 0",
}));

const OverallStatCard = styled(Paper)(({ theme, gradient }) => ({
  padding: "20px 16px",
  borderRadius: "8px",
  background: gradient || "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "#ffffff",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  cursor: "default",
  height: "100%",
  border: "none",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 8px 16px rgba(0,0,0,0.15)",
  },
}));

const StatIconBox = styled(Box)({
  width: "48px",
  height: "48px",
  borderRadius: "50%",
  background: "rgba(255, 255, 255, 0.2)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "12px",
});

const StatValue = styled(Typography)({
  fontSize: "28px",
  fontWeight: 800,
  lineHeight: 1.2,
});

const StatLabel = styled(Typography)({
  fontSize: "12px",
  opacity: 0.8,
  textTransform: "uppercase",
  letterSpacing: "1px",
  fontWeight: 600,
  textAlign: "center",
  marginTop: "8px",
});

const StyledTableContainer = styled(TableContainer)({
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  overflow: "hidden",
  marginTop: "16px",
});

const StyledTableHead = styled(TableHead)({
  backgroundColor: "#f8f9fa",
  '& .MuiTableCell-head': {
    fontWeight: 600,
    fontSize: "13px",
    color: "#4b5563",
    padding: "12px 16px",
    borderBottom: "1px solid #e5e7eb",
    backgroundColor: "#f8f9fa",
  },
});

const StyledTableCell = styled(TableCell)({
  padding: "12px 16px",
  fontSize: "14px",
  borderBottom: "1px solid #e5e7eb",
});

const StyledTableRow = styled(TableRow)({
  '&:hover': {
    backgroundColor: "#f9fafb",
  },
});

const LGAStatsCard = styled(Card)({
  borderRadius: "8px",
  border: "1px solid #e5e7eb",
  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
    borderColor: "#243b5a",
  },
});

const SectionTitle = styled(Typography)({
  fontSize: "1rem",
  fontWeight: 600,
  color: "#243b5a",
  marginBottom: "16px",
  borderBottom: "1px solid #e5e7eb",
  paddingBottom: "8px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const SummaryRow = styled(TableRow)({
  backgroundColor: "#f0f9ff",
  '&:hover': {
    backgroundColor: "#f0f9ff",
  },
  '& .MuiTableCell-root': {
    fontWeight: 700,
    borderTop: "2px solid #243b5a",
  },
});

// ============================================
// Types
// ============================================
interface LGAStat {
  lgaName: string;
  lgaCode: string;
  totalPublicSchools: number;
  totalPrivateSchools: number;
  activePublicSchools: number;
  activePrivateSchools: number;
}

interface OverallStats {
  totalPublicSchools: number;
  totalPrivateSchools: number;
  activePublicSchools: number;
  activePrivateSchools: number;
  totalSchoolsAll: number;
  activeSchoolsAll: number;
}

interface StatsResponse {
  status: boolean;
  data: LGAStat[];
  overall: OverallStats;
}

const SchoolActivityStats: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedLGA, setSelectedLGA] = useState<string | null>(null);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${baseUrl()}Admin2s/getSchoolActivityStats22`);
      if (response.data.status && response.data.data) {
        setStats(response.data);
      } else {
        setError("Invalid data format received from server");
      }
    } catch (error) {
      console.error("Error fetching school activity stats:", error);
      setError("Failed to load statistics. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  // Helper functions with explicit number conversion to prevent string concatenation
  const toNumber = (value: any): number => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  const calculateTotalSchools = (lga: LGAStat): number => {
    return toNumber(lga.totalPublicSchools) + toNumber(lga.totalPrivateSchools);
  };

  const calculateActiveSchools = (lga: LGAStat): number => {
    return toNumber(lga.activePublicSchools) + toNumber(lga.activePrivateSchools);
  };

  const calculateInactiveSchools = (lga: LGAStat): number => {
    return calculateTotalSchools(lga) - calculateActiveSchools(lga);
  };

  // Export to CSV function
  const handleExportCSV = () => {
    if (!lgaStats || lgaStats.length === 0) {
      setError("No data to export");
      return;
    }

    // Calculate totals for summary row
    const totals = {
      totalPublic: lgaStats.reduce((sum, lga) => sum + toNumber(lga.totalPublicSchools), 0),
      activePublic: lgaStats.reduce((sum, lga) => sum + toNumber(lga.activePublicSchools), 0),
      totalPrivate: lgaStats.reduce((sum, lga) => sum + toNumber(lga.totalPrivateSchools), 0),
      activePrivate: lgaStats.reduce((sum, lga) => sum + toNumber(lga.activePrivateSchools), 0),
      totalActive: lgaStats.reduce((sum, lga) => sum + calculateActiveSchools(lga), 0),
      totalInactive: lgaStats.reduce((sum, lga) => sum + calculateInactiveSchools(lga), 0),
    };

    // Prepare CSV headers
    const headers = [
      'S/N',
      'LGA NAME',
      'Total Public Schools',
      'Active Public Schools',
      'Total Private Schools',
      'Active Private Schools',
      'Total Active Schools',
      'Total Inactive Schools'
    ];

    // Prepare CSV rows
    const rows = lgaStats.map((lga, index) => [
      index + 1,
      lga.lgaName,
      toNumber(lga.totalPublicSchools),
      toNumber(lga.activePublicSchools),
      toNumber(lga.totalPrivateSchools),
      toNumber(lga.activePrivateSchools),
      calculateActiveSchools(lga),
      calculateInactiveSchools(lga)
    ]);

    // Add summary row
    rows.push([
      '',
      'TOTAL (ALL LGAs)',
      totals.totalPublic,
      totals.activePublic,
      totals.totalPrivate,
      totals.activePrivate,
      totals.totalActive,
      totals.totalInactive
    ]);

    // Convert to CSV format
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => 
        typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
      ).join(','))
    ].join('\n');

    // Add BOM for UTF-8 encoding to handle special characters
    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `lga_school_statistics_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading && !stats) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!stats || !stats.overall) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        No statistics data available
      </Alert>
    );
  }

  const { overall, data: lgaStats } = stats;

  // Calculate totals for summary row
  const totals = {
    totalPublic: lgaStats.reduce((sum, lga) => sum + toNumber(lga.totalPublicSchools), 0),
    activePublic: lgaStats.reduce((sum, lga) => sum + toNumber(lga.activePublicSchools), 0),
    totalPrivate: lgaStats.reduce((sum, lga) => sum + toNumber(lga.totalPrivateSchools), 0),
    activePrivate: lgaStats.reduce((sum, lga) => sum + toNumber(lga.activePrivateSchools), 0),
    totalActive: lgaStats.reduce((sum, lga) => sum + calculateActiveSchools(lga), 0),
    totalInactive: lgaStats.reduce((sum, lga) => sum + calculateInactiveSchools(lga), 0),
  };

  // Calculate overall totals for summary footer
  const overallTotalSchools = lgaStats.reduce((sum, lga) => sum + calculateTotalSchools(lga), 0);
  const overallActiveSchools = lgaStats.reduce((sum, lga) => sum + calculateActiveSchools(lga), 0);
  const overallInactiveSchools = overallTotalSchools - overallActiveSchools;

  return (
    <StatsContainer>
      {/* Header with Title and Actions */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar sx={{ bgcolor: "#243b5a", width: 40, height: 40 }}>
            <BarChart sx={{ fontSize: 22, color: "#ffffff" }} />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#243b5a", letterSpacing: "0.5px" }}>
              School Activity Statistics
            </Typography>
            <Typography variant="caption" sx={{ color: "#6b7280" }}>
              Comprehensive overview of public and private school activities
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Refresh Stats">
            <IconButton 
              onClick={fetchStats} 
              size="small" 
              sx={{ 
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                backgroundColor: "#ffffff",
                '&:hover': { backgroundColor: "#f9fafb" }
              }}
            >
              <Refresh fontSize="small" sx={{ color: "#6b7280" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export to CSV">
            <IconButton 
              onClick={handleExportCSV} 
              size="small" 
              sx={{ 
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                backgroundColor: "#ffffff",
                '&:hover': { backgroundColor: "#f9fafb" }
              }}
            >
              <GetApp fontSize="small" sx={{ color: "#6b7280" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={showBreakdown ? "Hide LGA Breakdown" : "Show LGA Breakdown"}>
            <IconButton 
              onClick={() => setShowBreakdown(!showBreakdown)} 
              size="small" 
              sx={{ 
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                backgroundColor: "#ffffff",
                '&:hover': { backgroundColor: "#f9fafb" }
              }}
            >
              {showBreakdown ? <ExpandLess sx={{ color: "#6b7280" }} /> : <ExpandMore sx={{ color: "#6b7280" }} />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Overall Statistics Section */}
      <SectionTitle>
        <AccountBalance sx={{ fontSize: 20, color: "#243b5a" }} />
        OVERALL STATISTICS (All LGAs Combined)
      </SectionTitle>
      
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {/* Total Public Schools Overall */}
        <Grid item xs={12} sm={6} md={3}>
          <OverallStatCard gradient="linear-gradient(135deg, #3a7bd5 0%, #00d2ff 100%)">
            <StatIconBox>
              <AccountBalance sx={{ fontSize: 24 }} />
            </StatIconBox>
            <StatValue>{toNumber(overall.totalPublicSchools).toLocaleString()}</StatValue>
            <StatLabel>Total Public Schools</StatLabel>
          </OverallStatCard>
        </Grid>

        {/* Active Public Schools Overall */}
        <Grid item xs={12} sm={6} md={3}>
          <OverallStatCard gradient="linear-gradient(135deg, #000428 0%, #004e92 100%)">
            <StatIconBox>
              <CheckCircle sx={{ fontSize: 24 }} />
            </StatIconBox>
            <StatValue>{toNumber(overall.activePublicSchools).toLocaleString()}</StatValue>
            <StatLabel>Active Public Schools</StatLabel>
          </OverallStatCard>
        </Grid>

        {/* Total Private Schools Overall */}
        <Grid item xs={12} sm={6} md={3}>
          <OverallStatCard gradient="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)">
            <StatIconBox>
              <Business sx={{ fontSize: 24 }} />
            </StatIconBox>
            <StatValue>{toNumber(overall.totalPrivateSchools).toLocaleString()}</StatValue>
            <StatLabel>Total Private Schools</StatLabel>
          </OverallStatCard>
        </Grid>

        {/* Active Private Schools Overall */}
        <Grid item xs={12} sm={6} md={3}>
          <OverallStatCard gradient="linear-gradient(135deg, #1D2671 0%, #C33764 100%)">
            <StatIconBox>
              <CheckCircle sx={{ fontSize: 24 }} />
            </StatIconBox>
            <StatValue>{toNumber(overall.activePrivateSchools).toLocaleString()}</StatValue>
            <StatLabel>Active Private Schools</StatLabel>
          </OverallStatCard>
        </Grid>
      </Grid>

      {/* LGA Statistics Section */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <SectionTitle sx={{ mb: 0 }}>
          <LocationCity sx={{ fontSize: 20, color: "#243b5a" }} />
          LOCAL GOVERNMENT AREA STATISTICS
        </SectionTitle>
        {showBreakdown && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<GetApp />}
            onClick={handleExportCSV}
            sx={{ textTransform: "none", borderColor: "#e5e7eb", color: "#243b5a" }}
          >
            Export Table
          </Button>
        )}
      </Box>
      
      {/* LGA Cards Grid View */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {lgaStats.map((lga, index) => {
          const totalSchools = calculateTotalSchools(lga);
          const activeSchools = calculateActiveSchools(lga);
          const inactiveSchools = calculateInactiveSchools(lga);
          
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={lga.lgaCode || index}>
              <LGAStatsCard onClick={() => setSelectedLGA(selectedLGA === lga.lgaName ? null : lga.lgaName)}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: "#e3f2fd", color: "#243b5a" }}>
                      <Domain sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#243b5a", fontSize: "14px" }}>
                      {lga.lgaName}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: "#6b7280" }}>Total Public:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#1976d2" }}>
                        {toNumber(lga.totalPublicSchools).toLocaleString()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="caption" sx={{ color: "#6b7280" }}>Active Public:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#2E7D32" }}>
                        {toNumber(lga.activePublicSchools).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: "#6b7280" }}>Total Private:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#7B1FA2" }}>
                        {toNumber(lga.totalPrivateSchools).toLocaleString()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="caption" sx={{ color: "#6b7280" }}>Active Private:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#2E7D32" }}>
                        {toNumber(lga.activePrivateSchools).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid #e5e7eb" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: "#243b5a" }}>
                        Total Active:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#059669" }}>
                        {activeSchools.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: "#243b5a" }}>
                        Total Inactive:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#dc2626" }}>
                        {inactiveSchools.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </LGAStatsCard>
            </Grid>
          );
        })}
      </Grid>

      {/* Detailed Breakdown Table */}
      <Collapse in={showBreakdown}>
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "#243b5a" }}>
            Detailed LGA Breakdown
          </Typography>
          <StyledTableContainer component={Paper}>
            <Table size="small">
              <StyledTableHead>
                <TableRow>
                  <TableCell width="50px">S/N</TableCell>
                  <TableCell>LGA NAME</TableCell>
                  <TableCell align="center" width="130px">Total Public</TableCell>
                  <TableCell align="center" width="130px">Active Public</TableCell>
                  <TableCell align="center" width="130px">Total Private</TableCell>
                  <TableCell align="center" width="130px">Active Private</TableCell>
                  <TableCell align="center" width="120px">Total Active</TableCell>
                  <TableCell align="center" width="120px">Total Inactive</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {lgaStats && lgaStats.length > 0 ? (
                  <>
                    {lgaStats.map((lga, index) => {
                      const activeSchools = calculateActiveSchools(lga);
                      const inactiveSchools = calculateInactiveSchools(lga);
                      
                      return (
                        <StyledTableRow key={lga.lgaCode || index}>
                          <StyledTableCell>{index + 1}</StyledTableCell>
                          <StyledTableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar sx={{ width: 28, height: 28, bgcolor: "#e3f2fd", color: "#243b5a" }}>
                                <Domain sx={{ fontSize: 14 }} />
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: "#243b5a" }}>
                                {lga.lgaName}
                              </Typography>
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Chip 
                              label={toNumber(lga.totalPublicSchools).toLocaleString()} 
                              size="small" 
                              sx={{ bgcolor: "#e3f2fd", color: "#1976d2", fontWeight: 700, minWidth: "70px" }} 
                            />
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Chip 
                              label={toNumber(lga.activePublicSchools).toLocaleString()} 
                              size="small" 
                              sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700, minWidth: "70px" }} 
                            />
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Chip 
                              label={toNumber(lga.totalPrivateSchools).toLocaleString()} 
                              size="small" 
                              sx={{ bgcolor: "#F3E5F5", color: "#7B1FA2", fontWeight: 700, minWidth: "70px" }} 
                            />
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Chip 
                              label={toNumber(lga.activePrivateSchools).toLocaleString()} 
                              size="small" 
                              sx={{ bgcolor: "#FFF3E0", color: "#EF6C00", fontWeight: 700, minWidth: "70px" }} 
                            />
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Chip 
                              label={activeSchools.toLocaleString()} 
                              size="small" 
                              sx={{ bgcolor: "#DCFCE7", color: "#059669", fontWeight: 700, minWidth: "70px" }} 
                            />
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Chip 
                              label={inactiveSchools.toLocaleString()} 
                              size="small" 
                              sx={{ bgcolor: "#FEE2E2", color: "#DC2626", fontWeight: 700, minWidth: "70px" }} 
                            />
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                    
                    {/* Summary Row - TOTAL */}
                    <SummaryRow>
                      <StyledTableCell colSpan={2}>
                        <Typography sx={{ fontWeight: 700, color: "#243b5a" }}>
                          TOTAL (All LGAs)
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Chip 
                          label={totals.totalPublic.toLocaleString()} 
                          size="small" 
                          sx={{ bgcolor: "#e3f2fd", color: "#1976d2", fontWeight: 800, minWidth: "70px" }} 
                        />
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Chip 
                          label={totals.activePublic.toLocaleString()} 
                          size="small" 
                          sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 800, minWidth: "70px" }} 
                        />
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Chip 
                          label={totals.totalPrivate.toLocaleString()} 
                          size="small" 
                          sx={{ bgcolor: "#F3E5F5", color: "#7B1FA2", fontWeight: 800, minWidth: "70px" }} 
                        />
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Chip 
                          label={totals.activePrivate.toLocaleString()} 
                          size="small" 
                          sx={{ bgcolor: "#FFF3E0", color: "#EF6C00", fontWeight: 800, minWidth: "70px" }} 
                        />
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Chip 
                          label={totals.totalActive.toLocaleString()} 
                          size="small" 
                          sx={{ bgcolor: "#DCFCE7", color: "#059669", fontWeight: 800, minWidth: "70px" }} 
                        />
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Chip 
                          label={totals.totalInactive.toLocaleString()} 
                          size="small" 
                          sx={{ bgcolor: "#FEE2E2", color: "#DC2626", fontWeight: 800, minWidth: "70px" }} 
                        />
                      </StyledTableCell>
                    </SummaryRow>
                  </>
                ) : (
                  <StyledTableRow>
                    <StyledTableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No LGA data available</Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </Box>
      </Collapse>

      {/* Summary Footer */}
      <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #e5e7eb" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" sx={{ color: "#6b7280", display: "block" }}>
              Total LGAs: {lgaStats.length}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" sx={{ color: "#6b7280", display: "block" }}>
              Total Schools (All LGAs): {overallTotalSchools.toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" sx={{ color: "#059669", display: "block", textAlign: { xs: "left", md: "right" }, fontWeight: 600 }}>
              Total Active: {overallActiveSchools.toLocaleString()}
            </Typography>
            <Typography variant="caption" sx={{ color: "#dc2626", display: "block", textAlign: { xs: "left", md: "right" }, fontWeight: 600 }}>
              Total Inactive: {overallInactiveSchools.toLocaleString()}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </StatsContainer>
  );
};

export default SchoolActivityStats;